import type { AppDispatch } from '@/app/store/store';
import type { PlpListingRequestArgs } from '../utils/plpListingRequest';
import {
  mergePlpSplitResponses,
  productListingToPlainObject,
} from '../mappers/plp.mapper';
import { ProductListing } from '../../domain/models/ProductListing';
import type {
  PlpListingResponseDTO,
  PlpFiltersResponseDTO,
} from '../dto/plp.dto';
import { plpApi } from './plpListingApi';
import type { SeoRepository, SeoResult } from '@/shared/features/seo/domain/interfaces/SeoRepository';
import { plainToListingSeo, adaptFaqsFromPlain } from '@/shared/features/seo/infra/mappers/seo.mapper';

export type FetchPlpListingOptions = {
  signal?: AbortSignal;
};

async function dispatchQuery<T>(
  dispatch: AppDispatch,
  initiateResult: unknown,
  signal?: AbortSignal
): Promise<T> {
  const pendingQuery = dispatch(
    initiateResult as unknown as Parameters<AppDispatch>[0]
  ) as unknown as { unwrap: () => Promise<T>; abort: () => void };

  const onAbort = () => {
    pendingQuery.abort();
  };
  if (signal) {
    if (signal.aborted) {
      onAbort();
    } else {
      signal.addEventListener('abort', onAbort);
    }
  }
  try {
    return await pendingQuery.unwrap();
  } finally {
    if (signal) {
      signal.removeEventListener('abort', onAbort);
    }
  }
}

function isAbortError(error: unknown, signal?: AbortSignal): boolean {
  return (
    Boolean(signal?.aborted) ||
    (error instanceof DOMException && error.name === 'AbortError') ||
    (typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      (error as { name: string }).name === 'AbortError')
  );
}

export async function fetchPlpListingPage(
  dispatch: AppDispatch,
  params: PlpListingRequestArgs,
  seoRepo: SeoRepository,
  options?: FetchPlpListingOptions
): Promise<ProductListing> {
  const signal = options?.signal;
  try {
    const pageNumber = params.pageNumber ?? 1;
    const listingDto = await dispatchQuery<PlpListingResponseDTO>(
      dispatch,
      plpApi.endpoints.getListing.initiate(params),
      signal
    );

    if (pageNumber > 1) {
      return mergePlpSplitResponses(listingDto ?? {});
    }

    const initialFiltersParams: PlpListingRequestArgs = {
      ...params,
      is_for_filter_change: false,
    };

    const [filtersDto, seoResult] = await Promise.all([
      dispatchQuery<PlpFiltersResponseDTO>(
        dispatch,
        plpApi.endpoints.getListingFilters.initiate(initialFiltersParams),
        signal
      ),
      seoRepo.fetchSeoData(initialFiltersParams, { signal }),
    ]);

    return mergePlpSplitResponses(listingDto ?? {}, filtersDto ?? {}, seoResult);
  } catch (error) {
    if (isAbortError(error, signal)) {
      throw new DOMException('Aborted', 'AbortError');
    }
    throw error;
  }
}

/** Merge split SSR responses into plain object for Redux / ListingPageResult conversion. */
export function mergePlpSplitResponsesToPlain(
  listing: Record<string, unknown>,
  filters?: Record<string, unknown>,
  seo?: Record<string, unknown>
): Record<string, unknown> {
  const seoResult: SeoResult | undefined = seo
    ? { listingSeo: plainToListingSeo(seo), faqs: adaptFaqsFromPlain(seo) }
    : undefined;
  const domain = mergePlpSplitResponses(listing, filters, seoResult);
  return productListingToPlainObject(domain);
}
